<template>
  <div ref="htmlContainer"></div>
</template>

<script setup>
import { ref, onMounted, watch, computed } from 'vue'
import { useRuntimeConfig } from '#app'

const config = useRuntimeConfig()

const props = defineProps({
  htmlContent: {
    type: String,
    required: true
  }
})

const htmlContainer = ref(null)

/**
 * SECURITY UPDATE: April 2025
 * Instead of directly accessing the sensitive STRAPI_URL from public config,
 * we now use our secure media proxy endpoint.
 *
 * This approach eliminates exposure of sensitive backend URLs in client-side code.
 */
const getSecureMediaUrl = (relativePath) => {
  // If the path is already absolute (starts with http), return it as is
  if (relativePath.startsWith('http')) {
    return relativePath
  }
  
  // Normalize the path to ensure it starts with a slash
  const normalizedPath = relativePath.startsWith('/') ? relativePath : `/${relativePath}`
  
  // Use our secure media proxy endpoint
  return `/api/strapi/media${normalizedPath}`
}

const renderHtml = () => {
  if (htmlContainer.value && props.htmlContent) {
    htmlContainer.value.innerHTML = props.htmlContent
    
    // Handle dynamic Strapi image URLs
    const images = htmlContainer.value.querySelectorAll('.dynamic-strapi-image')
    images.forEach(img => {
      if (img.getAttribute('data-path')) {
        const relativePath = img.getAttribute('data-path')
        // Use secure media proxy instead of direct Strapi URL
        img.setAttribute('src', getSecureMediaUrl(relativePath))
      }
    })
    
    // Execute any scripts in the HTML content
    const scripts = htmlContainer.value.querySelectorAll('script')
    scripts.forEach(script => {
      const newScript = document.createElement('script')
      Array.from(script.attributes).forEach(attr => {
        newScript.setAttribute(attr.name, attr.value)
      })
      newScript.textContent = script.textContent
      script.parentNode.replaceChild(newScript, script)
    })
  }
}

onMounted(renderHtml)

watch(() => props.htmlContent, renderHtml)
</script>

<style scoped>
/* Ensure styles from parent components don't interfere */
:deep(.plan-features-list) {
  list-style: none;
  padding-left: 0;
}

:deep(.plan-features-list li) {
  display: flex;
  align-items: flex-start;
}

:deep(.dynamic-strapi-image) {
  flex-shrink: 0;
}

:deep(.prvidr-row) {
  display: flex;
  margin-bottom: 12px;
}

:deep(.prvidr-cell) {
  display: table-cell;
  vertical-align: middle;
  padding-right: 10px;
}

:deep(.temp-icon) {
  width: 24px;
  padding-right: 10px;
}

:deep(.icon-normal) {
  width: 20px;
  height: 20px;
  object-fit: contain;
}

:deep(.tooltip-trigger) {
  color: #9900F1; /* OnePass purple */
  text-decoration: underline;
  cursor: pointer;
}

:deep(.rollover-desc), :deep(.small-text) {
  font-size: 12px;
  color: #666;
}
</style>
